import type { ChangeEvent } from 'react';
import { useCallback } from 'react';
import { FormField, Paragraph, TimeInput } from '@george-labs.com/design-system';
import type { TimeInputProps } from '@george-labs.com/design-system/types/components/Input';

import type { FormFieldProps } from '@shared/form2';
import { shouldShowError } from '@shared/form2';
import { useField } from '@luna/form2';
import { DateFormat, formatDate, isValidDate } from '@luna/date';

interface Props extends FormFieldProps {
    inputProps?: Partial<TimeInputProps>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

type FieldValue = string | undefined | null;

const LABEL_COLUMN_WIDTH = 4;

const getDate = (date: FieldValue): Date | undefined => {
    if (!date) {
        return undefined;
    }
    return new Date(date);
};

export const FormTimeField = ({
    label,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    fieldName,
    required,
    inputProps,
    warning,
    info,
    labelPosition,
    description,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
}: Props) => {
    const [{ onChange, value: fieldValue }, { touched, error }] = useField<FieldValue>(fieldName, {
        shouldClearOnUnmount,
    });

    const isFieldErrorVisible = shouldShowError(isErrorVisible, touched, error);

    const handleChange = useCallback(
        (time: Date | null): void => {
            const isValid = time && isValidDate(time);
            const value = isValid ? formatDate(time, DateFormat.ISO_DATE_TIME_FORMAT) : null;
            onChange({ target: { value } } as ChangeEvent<HTMLInputElement>);
        },
        [onChange],
    );

    return (
        <FormField
            required={required}
            description={description}
            input={
                <TimeInput
                    time={getDate(fieldValue)}
                    onTimeChange={handleChange}
                    data-cy={dataCy}
                    {...inputProps}
                />
            }
            labelPosition={labelPosition}
            label={label}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            warning={warning}
            info={info}
            addon={addon}
        />
    );
};

FormTimeField.LABEL_POSITION = FormField.LABEL_POSITION;
